/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './ControllerComponent';

const ControllerComponentManager: ComponentManagerFn<'ControllerComponent'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ControllerComponent'>(
      platform,
      'ControllerComponent',
    ),
    type: 'ControllerComponent',
    name: 'Controller Component',
    defaultValue,
    props: {
      controllerComponentFunction: {
        label: 'Controller Component Function',
        type: 'controllerFunction',
        category: 'Controllers',
        required: true,
        alwaysShow: true,
      },
    },
    category: 'Custom',
    icon: { iconName: 'react', iconType: 'brands' },
    description: `**Overview**<br/>
The Controller Component provides developers with maximum control and flexibility within the low-code environment. While the platform provides a rich library of standard components (Buttons, Grids, Charts), there are times when you need a highly specialized UI—such as a custom D3 visualization, a complex third-party widget, or a completely unique interaction model—that cannot be built using standard blocks.
The Controller Component allows you to render a Custom React Component directly on the page, with its logic and rendering defined entirely in your Page Controller code.

**1. Configuration**<br/>
Because the logic lives in code, the configuration in the designer is intentionally minimal. There is only one required property.
* **Controller Component Function:** Select the function name exported from your Page Controller file.
* **Requirement:** This function must be a valid React Functional Component (it must return JSX/React Elements).
* **Behavior:** The platform locates this function at runtime and renders it inside the container.

**2. Implementing the Code**<br/>
To make this work, you must switch to the Code Editor (Page Controller) and define the component.


**3. Props & Context**<br/>
The platform automatically injects useful context into your custom component via props. Your code can access these to interact with the broader application.
* **appUid:** The unique ID of the current application.
* **pageId:** The unique ID of the current page.
* **itemId:** The ID assigned to this Controller Component in the designer.
* **width / height:** The dimensions allocated to the component by the layout engine.

**4. Best Practices**<br/>
* **Use Sparingly:** Only use this component when standard UI elements absolutely cannot solve the problem. Custom code requires maintenance and bypasses some of the platform's automatic optimizations.
* **State Management:** Since this is a standard React component, you can use hooks like \`useState\` and \`useEffect\`. However, to interact with the platform's Data Stores, you should use the platform's provided SDK/API hooks.
* **Styling:** The component is wrapped in a standard Box. You can pass styles, but be mindful of the parent layout constraints (Flex/Grid) defined in the designer.`,
  };
};

export default ControllerComponentManager;
